import React, { memo, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { Bubble } from 'tea-component';
import agentRefArrow from '@/pages/aigc/assets/svg/agent-ref-arrow.svg';
import excelIcon from '@/pages/aigc/assets/svg/excel.svg';
import posterStyleIcon from '@/pages/aigc/assets/svg/poster-style-icon.svg';
import chartDashboardIcon from '@/pages/aigc/assets/svg/chart-dashboard.svg';
import defaultGameIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import EllipsisHover from '@gp/components/EllipsisHover';
import BasicImage from '@home/components/BasicImage';
import { usePosterTemplateData } from '@/pages/aigc/pages/Agent/Home/hooks/usePosterTemplateData';
import { ChartTagTooltipContent } from '@/pages/aigc/pages/Agent/components/QAInput/ChartPopover/components/SelectedChartTag';
import { getChartTypeIcon } from '@/pages/aigc/pages/Agent/components/QAInput/ChartPopover/utils/chartTypeIconMap';
import { LANGUAGE_EN } from '@home/_config/config';

export interface IFrontendMetadata {
  type?: 'magic_poster' | 'ai_excel' | 'game_context' | 'custom_dashboard' | string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  poster_template?: 0 | 1 | 2;
  templateId?: string;
  queryChartsByGame?: Array<{
    gameCode: string;
    gameName?: string;
    gameIcon?: string;
    charts: Array<{
      chartId: string;
      type: string;
      nameCN: string;
      nameEN: string;
      chartTypeIcon?: string;
    }>;
  }>;
  // 以下字段已废弃，保留仅为兼容历史数据
  // eslint-disable-next-line @typescript-eslint/naming-convention
  field_name?: string;
  coverUrl?: string;
  titleCn?: string;
  titleEn?: string;
  [key: string]: any;
}

interface IFieldRefTagProps {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  frontend_metadata: IFrontendMetadata;
}

/** 所有子 tag 组件共用的 props */
interface ITagInnerProps {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  frontend_metadata: IFrontendMetadata;
}

/** opinion_pc 类型：复用原有样式，在箭头与文案之间加游戏图片 */
const OpinionPCRefTag: React.FC<ITagInnerProps> = ({ frontend_metadata }) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { field_name: fieldName, prefixImage } = frontend_metadata;
  if (!fieldName) return null;
  return (
    <RefTagWrapper>
      <BasicImage
        src={agentRefArrow}
        alt=""
        className="dba-question-field-ref__arrow"
        errorSrc={agentRefArrow}
      />
      <BasicImage src={prefixImage || ''} className="dba-question-field-ref__icon dba-question-field-ref__icon--opinion-pc" />
      <EllipsisHover text={`「${fieldName}」`} className="dba-question-field-ref__text" />
    </RefTagWrapper>
  );
};

interface IRefTagWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/** 公共外壳：箭头图标 + 内容插槽 */
const RefTagWrapper: React.FC<IRefTagWrapperProps> = ({ children, ...wrapperProps }) => (
  <div className="dba-question-field-ref" {...wrapperProps}>
    {/* <img src={agentRefArrow} alt="" className="dba-question-field-ref__arrow" /> */}
    <span className="dba-question-field-ref__content">{children}</span>
  </div>
);

/** magic_poster 类型：紫色图标 + 封面图 + 国际化风格名 */
const MagicPosterRefTag: React.FC<ITagInnerProps> = ({ frontend_metadata }) => {
  const { formatMessage } = useIntl();
  const isEN = getLocale() === LANGUAGE_EN;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { poster_template, templateId, field_name: legacyFieldName } = frontend_metadata;

  const { data: templateList } = usePosterTemplateData();

  const posterData = useMemo(() => {
    // poster_template: 0=自定义, 1=预设模板, 2=自定义（聊天页面）
    const isCustom = poster_template === 0 || poster_template === 2;
    if (isCustom) {
      return {
        styleName: formatMessage({ id: 'dba_poster_style_custom' }),
        coverUrl: undefined,
      };
    }
    if (templateId && templateList?.[0]?.list?.length > 0) {
      const template = templateList[0].list.find(
        (t: any) => (t.templateId || t.id) === templateId,
      );
      if (template) {
        return {
          styleName: isEN ? template.titleEn : template.titleCn,
          coverUrl: isEN ? template.coverUrl_en : template.coverUrl_cn,
        };
      }
    }
    return null;
  }, [poster_template, templateId, templateList, isEN, formatMessage]);

  const fieldName = useMemo(() => {
    if (posterData) {
      return formatMessage({ id: 'dba_poster_style_ref' }, { styleName: posterData.styleName });
    }
    return legacyFieldName;
  }, [posterData, formatMessage, legacyFieldName]);

  if (!fieldName) return null;

  // 将「xxx」中的内容单独加粗，格式如：基于「核心指标概览型」风格生成
  const startIndex = fieldName.indexOf('「');
  const endIndex = fieldName.indexOf('」');
  const prefix = startIndex >= 0 ? fieldName.slice(0, startIndex + 1) : '';
  const boldText =
    startIndex >= 0 && endIndex > startIndex ? fieldName.slice(startIndex + 1, endIndex) : '';
  const suffix = endIndex >= 0 ? fieldName.slice(endIndex) : fieldName;

  return (
    <RefTagWrapper>
      <img src={agentRefArrow} alt="" className="dba-question-field-ref__arrow" />
      <BasicImage src={posterStyleIcon} className="dba-question-field-ref__icon" />
      <span className="dba-question-field-ref__text">
        {prefix}
        {posterData?.coverUrl && (
          <BasicImage src={posterData.coverUrl} className="dba-question-field-ref__cover" />
        )}
        <span className="dba-question-field-ref__bold">{boldText}</span>
        {suffix}
      </span>
    </RefTagWrapper>
  );
};

/** 游戏 / 经分等上下文引用：展示 field_name（如「xxx」） */
const GameContextRefTag: React.FC<ITagInnerProps> = ({ frontend_metadata }) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { field_name: fieldName } = frontend_metadata;
  if (!fieldName) return null;
  return (
    <RefTagWrapper>
      <BasicImage
        src={agentRefArrow}
        alt=""
        className="dba-question-field-ref__arrow"
        errorSrc={agentRefArrow}
      />
      <EllipsisHover text={`「${fieldName}」`} className="dba-question-field-ref__text" />
    </RefTagWrapper>
  );
};

/** ai_excel 及默认类型：箭头图标 + excel 图标 + 省略文字 */
const AiExcelRefTag: React.FC<ITagInnerProps> = ({ frontend_metadata }) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { field_name: fieldName } = frontend_metadata;
  if (!fieldName) return null;
  return (
    <RefTagWrapper>
      <BasicImage src={agentRefArrow} className="dba-question-field-ref__arrow" />
      <BasicImage src={excelIcon} className="dba-question-field-ref__icon" />
      <EllipsisHover text={fieldName} className="dba-question-field-ref__text" />
    </RefTagWrapper>
  );
};

/** custom_dashboard：箭头 + 游戏 icon + 游戏名 + 图表 icon（按游戏分组展示） */
const CustomDashboardRefTag: React.FC<ITagInnerProps> = ({ frontend_metadata }) => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const queryChartsByGame = frontend_metadata?.queryChartsByGame || [];

  const displayGameList = queryChartsByGame.filter((game) => (game?.charts || []).length > 0);

  if (displayGameList.length === 0) return null;

  const tooltipContent = (
    <ChartTagTooltipContent
      title={formatMessage({ id: 'dba_chart_ref_tooltip_title' })}
      list={displayGameList.map((game) => ({
        gameCode: game?.gameCode,
        gameName: game?.gameName || game?.gameCode || '-',
        gameIcon: game?.gameIcon,
        charts: (game?.charts || []).map((chart) => ({
          chartId: chart?.chartId,
          typeIcon: getChartTypeIcon(chart?.type, chart?.chartTypeIcon || chartDashboardIcon),
          label: isEN ? (chart?.nameEN || chart?.nameCN) : (chart?.nameCN || chart?.nameEN),
        })),
      }))}
    />
  );

  return (
    <RefTagWrapper>
      <BasicImage src={agentRefArrow} className="dba-question-field-ref__arrow" />
      <Bubble
        dark
        placement="left"
        flipped={false}
        arrowPointAtCenter
        closeOnScroll
        updateOnDimensionChange
        overlayClassName="dba-nav-menu-overlay dba-question-field-ref-tip"
        content={tooltipContent}
      >
        <div className="dba-question-field-ref__custom-dashboard">
          {displayGameList.map((game, gameIndex) => (
            <div className="dba-question-field-ref__game-row" key={game?.gameCode || game?.gameName || `game-${gameIndex}`}>
              <BasicImage
                src={game?.gameIcon || defaultGameIcon}
                className="dba-question-field-ref__icon"
                errorSrc={defaultGameIcon}
              />
              <span className="dba-question-field-ref__text dba-question-field-ref__game-name">
                {game?.gameName || game?.gameCode || '-'}
              </span>
              <span className="dba-question-field-ref__separator">-</span>
              <span className="dba-question-field-ref__chart-list">
                {game.charts.map((chart, chartIndex) => {
                  const chartName = isEN ? (chart?.nameEN || chart?.nameCN) : (chart?.nameCN || chart?.nameEN);
                  return (
                    <React.Fragment
                      key={chart?.chartId || `${game?.gameCode || game?.gameName || 'game'}-chart-${chartIndex}`}
                    >
                      <span className="dba-question-field-ref__chart-item">
                        <BasicImage
                          src={getChartTypeIcon(chart?.type, chart?.chartTypeIcon || chartDashboardIcon) || chartDashboardIcon}
                          className="dba-question-field-ref__chart-icon"
                          errorSrc={chartDashboardIcon}
                        />
                        <span className="dba-question-field-ref__chart-text">{chartName}</span>

                      </span>
                      {chartIndex !== game.charts.length - 1 && <span className="dba-question-field-ref__separator">|</span>}
                    </React.Fragment>
                  );
                })}
              </span>
            </div>
          ))}
        </div>
      </Bubble>
    </RefTagWrapper>
  );
};

/** type → 子组件映射表，新增类型只需在此扩展，需要兼容外部系统展示，以及各类风格，所以为方便维护，统一抽离，定制化各种小组件 */
const FIELD_REF_TAG_MAP: Record<string, React.FC<ITagInnerProps>> = {
  magic_poster: MagicPosterRefTag,
  ai_excel: AiExcelRefTag,
  bi_dashboard: GameContextRefTag,
  custom_dashboard: CustomDashboardRefTag,
  opinion_pc: OpinionPCRefTag,
  // aigallery_report：报告引用，展示与 bi_dashboard 一致（箭头 + 「页面名」）
  aigallery_report: GameContextRefTag,
};

// eslint-disable-next-line react/prop-types
const FieldRefTag = memo<IFieldRefTagProps>(({ frontend_metadata }) => {
  const { type } = frontend_metadata || {};
  const TagComponent = FIELD_REF_TAG_MAP[type ?? ''] ?? AiExcelRefTag;
  return <TagComponent frontend_metadata={frontend_metadata} />;
});

FieldRefTag.displayName = 'FieldRefTag';

export default FieldRefTag;
